/* ============================================================
   SENTINEL EF — sentinel-ef-calculator.js
   Real-world fence system calculator. Recommends strands from the animal,
   computes a quantified bill of materials, maps to live SKUs + merchant
   complement blocks, and adds the whole system to cart. Guards on
   [data-efcol-calc]; self-contained (no dependency on the collection JS).
   ============================================================ */

(function () {
  'use strict';

  var root = document.querySelector('[data-efcol-calc]');
  if (!root) return;

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }
  function arr(x) { return Array.isArray(x) ? x : []; }
  function money(cents) { return (cents / 100).toFixed(2).replace('.', ',') + ' $'; }
  function jLabel(j) { return (Math.round(j * 100) / 100).toString().replace('.', ','); }
  function parseJSON(sel) {
    var el = root.querySelector(sel);
    if (!el) return [];
    try { var v = JSON.parse(el.textContent); return Array.isArray(v) ? v : []; } catch (e) { return []; }
  }

  /* ---------- Animal profiles (recommendation, not a question) ---------- */
  var PROFILES = {
    chevaux:  { strands: 3, cls: 'ruban', why: 'Les chevaux repèrent mieux un ruban large ; 3 fils suffisent pour la plupart des enclos.' },
    bovins:   { strands: 3, cls: 'fil',   why: 'Les bovins se contiennent avec peu de fils ; ajoutez-en pour un long périmètre.' },
    moutons:  { strands: 5, cls: 'fil',   why: 'La laine isole : prévoyez plus de fils, rapprochés près du sol.' },
    chevres:  { strands: 5, cls: 'fil',   why: 'Les chèvres testent la clôture : 5 à 6 fils recommandés.' },
    porcs:    { strands: 3, cls: 'fil',   why: 'Les porcs poussent du museau : des fils bas et rapprochés.' },
    volaille: { strands: 2, cls: 'fil',   why: 'Pour la volaille, des fils bas découragent les prédateurs.' },
    chiens:   { strands: 3, cls: 'fil',   why: 'Deux à trois fils selon la taille du chien.' },
    faune:    { strands: 5, cls: 'fil',   why: 'Pour exclure la faune, une clôture haute à plusieurs fils.' }
  };
  var ANIMAL_LABEL = { chevaux: 'chevaux', bovins: 'bovins', moutons: 'moutons', chevres: 'chèvres', porcs: 'porcs', volaille: 'volaille', chiens: 'chiens', faune: 'la faune' };
  var CLASS_LABEL = { fil: 'Fil poly', ruban: 'Ruban', corde: 'Corde' };
  var SPACING = { plat: 12, vallonne: 9, accidente: 6 };
  var VEG = { chevaux: 1.3, bovins: 1.3, moutons: 1.6, chevres: 2.0, porcs: 1.8, volaille: 1.2, chiens: 1.3, faune: 2.0 };
  var SLACK = 1.10;
  var ROLE_FR = { 'tige-de-terre': 'Tiges de mise à la terre', 'cavalier': 'Câbles', 'testeur': 'Testeur', 'poteau': 'Poteaux', 'pancarte': 'Pancartes', 'autre': 'Complément' };

  var products = parseJSON('[data-calc-products]');
  var complements = parseJSON('[data-calc-complements]');
  var catalogEnergizers = parseJSON('[data-calc-energizers]');

  /* ---------- energizer pool: block energizers (explicit joules, e.g. Parmak) +
     catalogue collection (joules + power derived from the model name, e.g. Gallagher
     S20=0,2 J · M160=1,6 J · MBS400=4 J). Number in the model code ÷ 100 = joules. ---------- */
  function deriveJoules(title) {
    var m = /^\s*([A-Za-z]{1,3})(\d{1,4})\b/.exec(title || '');
    return m ? (parseInt(m[2], 10) / 100) : 0;
  }
  function derivePower(title) {
    var t = title || '';
    if (/solaire|solar/i.test(t)) return 'solaire';
    if (/double.{0,5}aliment/i.test(t)) return 'mixte';
    var m = /^\s*([A-Za-z]{1,3})\d/.exec(t);
    var pre = m ? m[1].toUpperCase() : '';
    if (pre === 'MBS' || pre === 'MB') return 'mixte';
    if (pre === 'B') return 'batterie';
    if (pre === 'S') return 'solaire';
    if (pre === 'M' || pre === 'MR') return 'secteur';
    if (/batterie|sur pile|\b12\s*v/i.test(t)) return 'batterie';
    if (/secteur|110\s*v|120\s*v|230\s*v|mains|prise/i.test(t)) return 'secteur';
    return 'secteur';
  }
  function matchesPower(sel, en) {
    if (!sel) return true;
    if (en.power === 'mixte') return sel !== 'solaire'; /* double-alimentation works on secteur OR batterie */
    return en.power === sel;
  }
  var energizerPool = (function () {
    var pool = [];
    complements.filter(function (c) { return c.kind === 'energizer_option' && c.joules > 0; }).forEach(function (c) {
      pool.push({ id: c.id, title: c.title, image: c.image, price: c.price, available: c.available, joules: c.joules, power: derivePower(c.title) });
    });
    catalogEnergizers.forEach(function (c) {
      var j = deriveJoules(c.title);
      if (j <= 0) return;
      pool.push({ id: c.id, title: c.title, image: c.image, price: c.price, available: c.available, joules: j, power: derivePower(c.title) });
    });
    return pool;
  })();

  var form = root.querySelector('[data-calc-form]');
  var elResults = root.querySelector('[data-calc-results]');
  var elReco = root.querySelector('[data-calc-reco]');
  var elSummary = root.querySelector('[data-calc-summary]');
  var elKit = root.querySelector('[data-calc-kit]');
  var elKitCount = root.querySelector('[data-calc-kit-count]');
  var elBom = root.querySelector('[data-calc-bom]');
  var elAdvisory = root.querySelector('[data-calc-advisory]');
  var elFooter = root.querySelector('[data-calc-footer]');
  var elTotalVal = root.querySelector('[data-calc-total-value]');
  var elFreeship = root.querySelector('[data-calc-freeship]');
  var elAdd = root.querySelector('[data-calc-add]');
  var elPlaceholder = root.querySelector('[data-calc-placeholder]');
  var elSticky = root.querySelector('[data-calc-sticky]');
  var elStickyTotal = root.querySelector('[data-calc-sticky-total]');
  var elStickyAdd = root.querySelector('[data-calc-sticky-add]');
  var elLead = root.querySelector('[data-calc-lead]');
  var freeshipCode = (elResults && elResults.getAttribute('data-freeship-code')) || '';

  /* captured for the lead-capture quote payload */
  var lastLines = [], lastTotal = 0, lastInputs = {};

  function q(sel) { return form.querySelector(sel); }
  function val(sel) { var el = q(sel); return el ? el.value : ''; }
  function num(sel, def) { var n = parseFloat(val(sel)); return isNaN(n) ? def : n; }
  function isOn(sel) { var el = q(sel); return el ? el.checked : false; }
  function animalVal() { var c = form.querySelector('input[name="efcalc-animal"]:checked'); return c ? c.value : ''; }
  function productById(id) { for (var i = 0; i < products.length; i++) if (String(products[i].id) === String(id)) return products[i]; return null; }

  /* ---------- SKU selection ---------- */
  function pickSKU(roles, cstr) {
    cstr = cstr || {};
    var cands = products.filter(function (p) {
      if (roles.indexOf(p.role) === -1) return false;
      if (cstr.mount && !(arr(p.mount).indexOf(cstr.mount) !== -1 || arr(p.mount).indexOf('universel') !== -1)) return false;
      if (cstr.cls && arr(p.conductor).indexOf(cstr.cls) === -1) return false;
      return true;
    });
    var live = cands.filter(function (p) { return p.available; });
    var pool = live.length ? live : cands;
    pool.sort(function (a, b) { return a.price - b.price; });
    return pool[0] || null;
  }

  /* ---------- cart drawer (FGE pattern) ---------- */
  function openCartDrawer() {
    var d = document.querySelector('cart-drawer');
    if (!d || typeof d.renderContents !== 'function') {
      var link = document.getElementById('cart-icon-bubble');
      if (link) link.click();
      return Promise.resolve();
    }
    if (d.classList.contains('is-empty')) d.classList.remove('is-empty');
    return fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (s) { d.renderContents({ sections: s }); })
      .catch(function () { var l = document.getElementById('cart-icon-bubble'); if (l) l.click(); });
  }

  /* ---------- conductor select ---------- */
  function initConductors() {
    var sel = q('[data-calc-conductor]');
    if (!sel) return;
    products.filter(function (p) { return p.category === 'fil-ruban'; })
      .sort(function (a, b) { return (a.available === b.available) ? a.price - b.price : (a.available ? -1 : 1); })
      .forEach(function (p) {
        var o = document.createElement('option');
        o.value = p.id;
        o.textContent = p.title + (p.available ? '' : ' (à commander)');
        sel.appendChild(o);
      });
  }
  function preselectConductor(cls) {
    var sel = q('[data-calc-conductor]');
    if (!sel) return;
    var cond = products.filter(function (p) { return p.category === 'fil-ruban'; });
    var match = cond.filter(function (p) { return p.available && arr(p.conductor).indexOf(cls) !== -1; })[0]
      || cond.filter(function (p) { return p.available; })[0] || cond[0];
    if (match) sel.value = match.id;
  }

  /* ---------- animal → recommendation ---------- */
  function onAnimal() {
    var animal = animalVal();
    var pr = PROFILES[animal];
    if (pr) {
      var sEl = q('[data-calc-strands]');
      if (sEl) sEl.value = pr.strands;
      preselectConductor(pr.cls);
      elReco.innerHTML =
        '<div class="efcol-calc__reco-stats">' +
          '<span class="efcol-calc__reco-stat"><b>' + pr.strands + '</b> fils recommandés</span>' +
          '<span class="efcol-calc__reco-stat">Conducteur : <b>' + (CLASS_LABEL[pr.cls] || pr.cls) + '</b></span>' +
        '</div>' +
        '<p class="efcol-calc__reco-why">' + pr.why + ' Ajustez dans les options avancées au besoin.</p>';
      elReco.hidden = false;
    } else {
      elReco.hidden = true;
    }
    compute();
  }

  /* ---------- the engine ---------- */
  function compute() {
    var animal = animalVal();
    var perimeter = num('[data-calc-perimeter]', 0);
    var shape = val('[data-calc-shape]');
    var terrain = val('[data-calc-terrain]');
    var gates = Math.max(0, Math.round(num('[data-calc-gates]', 0)));
    var corners = Math.max(0, Math.round(num('[data-calc-corners]', 0)));
    var profStrands = (PROFILES[animal] && PROFILES[animal].strands) || 2;
    var strands = Math.max(1, Math.round(num('[data-calc-strands]', profStrands)));
    var conductorId = val('[data-calc-conductor]');
    var mount = val('[data-calc-mount]');
    var power = val('[data-calc-power]') || 'secteur';
    var grounding = isOn('[data-calc-grounding]');
    var surge = isOn('[data-calc-surge]');
    var leadout = isOn('[data-calc-leadout]');
    lastInputs = { animal: animal, perimeter: perimeter, strands: strands, mount: mount, power: power, gates: gates, corners: corners };

    var conductor = conductorId ? productById(conductorId) : null;
    if (!conductor) {
      /* fall back to the first available conductor so the calc still runs */
      conductor = products.filter(function (p) { return p.category === 'fil-ruban' && p.available; })[0]
        || products.filter(function (p) { return p.category === 'fil-ruban'; })[0] || null;
    }
    if (!(animal && perimeter > 0 && conductor && mount)) { showEmpty(); return; }

    var conductorClass = arr(conductor.conductor)[0] || '';
    var rollLen = parseFloat(conductor.length) || 200;

    var spacing = SPACING[terrain] || 9;
    var posts = Math.ceil(perimeter / spacing) + (shape === 'ligne' ? 1 : 0);
    var endPosts = shape === 'ligne' ? 2 : 0;
    var lineIns = Math.max(0, posts - corners - endPosts) * strands;
    var cornerIns = corners * strands;
    var endIns = endPosts * strands;
    var gateHandles = gates * strands;
    var gateAnchors = gates * 2;
    var conductorLen = Math.ceil(perimeter * strands * SLACK);
    var rolls = Math.ceil(conductorLen / rollLen);
    var strainers = strands;
    var connectors = Math.max(0, rolls - strands) + (leadout ? 1 : 0);
    var jumpers = (leadout ? 1 : 0) + gates;
    var joulesNeeded = (perimeter * strands / 1000) * (VEG[animal] || 1.5);
    var rods = Math.max(3, Math.ceil(joulesNeeded));

    var defs = [
      { label: 'Conducteur', fixed: conductor, need: rolls, roll: true },
      { label: 'Isolateurs de ligne', roles: ['holder'], cstr: { mount: mount, cls: conductorClass }, need: lineIns },
      { label: 'Isolateurs de coin / ancrage', roles: ['gate-anchor'], cstr: { mount: mount }, need: cornerIns + gateAnchors },
      { label: 'Isolateurs d’extrémité', roles: ['end'], cstr: { cls: conductorClass }, need: endIns },
      { label: 'Poignées de portail', roles: ['handle'], cstr: { cls: conductorClass }, need: gateHandles },
      { label: 'Tendeurs', roles: ['strainer'], cstr: { cls: conductorClass }, need: strainers },
      { label: 'Connecteurs', roles: ['connector', 'clip'], cstr: { cls: conductorClass }, need: connectors },
      { label: 'Câbles cavaliers', roles: ['jumper'], cstr: {}, need: jumpers },
      { label: 'Bride de mise à la terre', roles: ['ground'], cstr: {}, need: grounding ? 1 : 0 },
      { label: 'Parafoudre', roles: ['surge'], cstr: {}, need: surge ? 1 : 0 }
    ];
    var sentinelLines = defs.map(function (d) {
      if (d.need <= 0) return null;
      var p = d.fixed || pickSKU(d.roles, d.cstr);
      var pack = (p && p.pack) ? p.pack : 1;
      var cartQty = d.roll ? d.need : Math.ceil(d.need / pack);
      return { label: d.label, product: p, need: d.need, pack: pack, roll: !!d.roll, cartQty: cartQty, live: !!(p && p.available), price: (p ? p.price * cartQty : 0) };
    }).filter(Boolean);

    var systemLines = [];
    var energizerJoules = Math.max(1, Math.ceil(joulesNeeded));
    var poweredEn = energizerPool.filter(function (c) { return matchesPower(power, c); });
    var enPool = poweredEn.length ? poweredEn : energizerPool;
    if (enPool.length) {
      var fitAll = enPool.filter(function (c) { return c.joules >= joulesNeeded; });
      var fitLive = fitAll.filter(function (c) { return c.available; });
      var fit = (fitLive.length ? fitLive : fitAll).sort(function (a, b) { return a.price - b.price; });
      var en = fit[0] || enPool.slice().sort(function (a, b) { return b.joules - a.joules; })[0];
      energizerJoules = en.joules;
      systemLines.push({ label: 'Électrificateur (' + jLabel(en.joules) + ' J)', product: en, need: 1, pack: 1, cartQty: 1, live: !!en.available, price: en.price });
    }
    var BASIS = { 'par-systeme': 1, 'par-tige': rods, 'par-portail': gates, 'par-coin': corners, 'par-fil': strands, 'par-poteau': posts };
    complements.filter(function (c) { return c.kind === 'complement'; }).forEach(function (c) {
      var qty = BASIS[c.basis] || 0;
      if (qty <= 0) return;
      systemLines.push({ label: c.label || ROLE_FR[c.role] || 'Complément', product: c, need: qty, pack: 1, cartQty: qty, live: !!c.available, price: c.price * qty });
    });

    var hasRod = complements.some(function (c) { return c.kind === 'complement' && c.role === 'tige-de-terre'; });
    var hasPost = complements.some(function (c) { return c.kind === 'complement' && c.role === 'poteau'; });
    var advisories = [];
    if (!enPool.length) advisories.push('Électrificateur recommandé : ≈ ' + Math.max(1, Math.ceil(joulesNeeded)) + ' J. À se procurer séparément.');
    if (grounding && !hasRod) advisories.push('Tiges de mise à la terre : ' + rods + ' (≈ 1 m, espacées de 3 m). À se procurer séparément.');
    if (!hasPost) advisories.push('Poteaux : ≈ ' + (posts + corners) + ' (déjà en place sur la plupart des clôtures existantes).');

    var stats = { strands: strands, perimeter: perimeter, joules: energizerJoules, parts: sentinelLines.length + systemLines.length };
    render(sentinelLines, systemLines, advisories, stats);
  }

  function qtyText(l) {
    if (l.roll) return l.cartQty + (l.cartQty > 1 ? ' rouleaux' : ' rouleau');
    if (l.pack > 1) return l.cartQty + (l.cartQty > 1 ? ' paquets' : ' paquet') + ' (besoin ' + l.need + ')';
    return l.need + (l.need > 1 ? ' unités' : ' unité');
  }
  function itemHtml(l) {
    var name = l.product ? l.product.title : '—';
    var img = (l.product && l.product.image) ? l.product.image : '';
    var thumb = img
      ? '<img src="' + img + '" alt="" loading="lazy" width="56" height="56">'
      : '<span class="efcol-calc__item-noimg" aria-hidden="true"></span>';
    var badge = l.product && !l.live ? '<span class="efcol-calc__badge">sur commande</span>' : '';
    var price = l.live ? money(l.price) : '<span class="efcol-calc__muted">sur commande</span>';
    return '<div class="efcol-calc__item' + (l.live ? '' : ' is-pending') + '">' +
      '<span class="efcol-calc__item-thumb">' + thumb + '</span>' +
      '<span class="efcol-calc__item-main">' +
        '<span class="efcol-calc__item-label">' + l.label + '</span>' +
        '<span class="efcol-calc__item-name">' + name + ' ' + badge + '</span>' +
        '<span class="efcol-calc__item-qty">' + qtyText(l) + '</span>' +
      '</span>' +
      '<span class="efcol-calc__item-price">' + price + '</span>' +
      '</div>';
  }
  function kitRowHtml(l) {
    var img = (l.product && l.product.image) ? l.product.image : '';
    var thumb = img
      ? '<img src="' + img + '" alt="" loading="lazy" width="34" height="34">'
      : '<span class="efcol-calc__kit-noimg" aria-hidden="true"></span>';
    var qty = l.roll ? (l.cartQty + ' rl') : (l.pack > 1 ? (l.cartQty + ' paq.') : (l.need + '×'));
    var price = l.live ? money(l.price) : '<span class="efcol-calc__muted">sur cmde</span>';
    return '<div class="efcol-calc__kit-row' + (l.live ? '' : ' is-pending') + '">' +
      '<span class="efcol-calc__kit-thumb">' + thumb + '</span>' +
      '<span class="efcol-calc__kit-info"><span class="efcol-calc__kit-name">' + l.label + '</span>' +
        '<span class="efcol-calc__kit-qty">' + qty + '</span></span>' +
      '<span class="efcol-calc__kit-price">' + price + '</span>' +
      '</div>';
  }
  function listHtml(title, lines) {
    if (!lines.length) return '';
    return '<div class="efcol-calc__tier"><h3 class="efcol-calc__tier-title">' + title + '</h3>' +
      '<div class="efcol-calc__list">' + lines.map(itemHtml).join('') + '</div></div>';
  }

  function render(sentinelLines, systemLines, advisories, stats) {
    var all = sentinelLines.concat(systemLines);
    var cartItems = all.filter(function (l) { return l.product && l.live && l.cartQty > 0; })
      .map(function (l) { return { id: parseInt(l.product.id, 10), quantity: l.cartQty }; });
    var total = all.filter(function (l) { return l.live; }).reduce(function (s, l) { return s + l.price; }, 0);
    var pending = all.some(function (l) { return l.product && !l.live; });

    elBom.innerHTML = '<h2 class="efcol-calc__detail-title">Le détail de votre système</h2>' +
      listHtml('Pièces Sentinel', sentinelLines) + listHtml('Système complet', systemLines) +
      (pending ? '<p class="efcol-calc__pending-note">Les articles « sur commande » ne sont pas encore en ligne : ils restent dans votre liste et s’ajoutent au panier dès qu’ils sont disponibles. Écrivez-nous pour les réserver.</p>' : '');
    elBom.hidden = false;
    if (elPlaceholder) elPlaceholder.hidden = true;

    if (advisories.length) {
      elAdvisory.innerHTML = '<p class="efcol-calc__advisory-title">À se procurer séparément</p><ul>' +
        advisories.map(function (a) { return '<li>' + a + '</li>'; }).join('') + '</ul>';
      elAdvisory.hidden = false;
    } else { elAdvisory.hidden = true; }

    elTotalVal.textContent = money(total);

    if (elSummary && stats) {
      var setStat = function (sel, v) { var e = elSummary.querySelector(sel); if (e) e.textContent = v; };
      setStat('[data-stat-strands]', stats.strands);
      setStat('[data-stat-perimeter]', stats.perimeter);
      setStat('[data-stat-joules]', (stats.joules != null ? jLabel(stats.joules) + ' J' : '—'));
      setStat('[data-stat-parts]', stats.parts);
    }

    if (elKit) elKit.innerHTML = all.map(kitRowHtml).join('');
    if (elKitCount) elKitCount.textContent = all.length;

    elFooter.hidden = false;
    elAdd.disabled = cartItems.length === 0;
    elAdd.__items = cartItems;

    lastLines = all; lastTotal = total;

    if (elFreeship) elFreeship.hidden = !freeshipCode;

    if (elSticky) {
      elSticky.hidden = cartItems.length === 0;
      if (elStickyTotal) elStickyTotal.textContent = money(total);
    }
  }

  function showEmpty() {
    elBom.hidden = true;
    elAdvisory.hidden = true;
    elFooter.hidden = true;
    elAdd.disabled = true;
    elAdd.__items = [];
    if (elSticky) elSticky.hidden = true;
    if (elPlaceholder) elPlaceholder.hidden = false;
  }

  function addAll() {
    var items = elAdd.__items || [];
    if (!items.length) return;
    var orig = elAdd.textContent;
    elAdd.disabled = true;
    elAdd.textContent = 'Ajout en cours...';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin',
      body: JSON.stringify({ items: items })
    })
      .then(function (res) { if (!res.ok) return res.json().then(function (e) { throw (e && e.description) || 'Ajout impossible.'; }); return res.json(); })
      .then(function () {
        /* tag the cart as a calculator kit + apply the Québec free-shipping code (best-effort) */
        var jobs = [
          fetch('/cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin', body: JSON.stringify({ attributes: { 'Source': 'Calculateur clôture' } }) }).catch(function () {})
        ];
        if (freeshipCode) jobs.push(fetch('/discount/' + encodeURIComponent(freeshipCode), { mode: 'no-cors', credentials: 'same-origin' }).catch(function () {}));
        return Promise.all(jobs);
      })
      .then(function () { return openCartDrawer(); })
      .then(function () { elAdd.textContent = orig; elAdd.disabled = false; })
      .catch(function (msg) { elAdd.textContent = orig; elAdd.disabled = false; alert(typeof msg === 'string' ? msg : 'Ajout impossible. Réessayez.'); });
  }

  /* ---------- lead capture → Klaviyo (client API) ---------- */
  function showLeadMsg(text, ok) {
    var m = elLead && elLead.querySelector('[data-calc-lead-msg]');
    if (!m) return;
    m.textContent = text;
    m.className = 'efcol-calc__lead-msg ' + (ok ? 'is-ok' : 'is-err');
    m.hidden = false;
  }
  function buildQuoteProps() {
    var lignes = lastLines.map(function (l) {
      var qty = l.roll ? (l.cartQty + ' rouleau') : (l.pack > 1 ? (l.cartQty + ' paq.') : (l.need + ' u.'));
      return l.label + ' : ' + (l.product ? l.product.title : '') + ' (' + qty + ')';
    });
    return {
      'Animal': lastInputs.animal || '',
      'Perimetre_m': lastInputs.perimeter || 0,
      'Fils': lastInputs.strands || 0,
      'Type_de_poteau': lastInputs.mount || '',
      'Alimentation': lastInputs.power || '',
      'Portails': lastInputs.gates || 0,
      'Coins': lastInputs.corners || 0,
      'Total_estime': money(lastTotal),
      'Nombre_de_pieces': lastLines.length,
      'Liste_de_materiel': lignes,
      'Page': window.location.href
    };
  }
  function leadSubmit(e) {
    e.preventDefault();
    var key = elLead.getAttribute('data-klaviyo-key');
    var listId = elLead.getAttribute('data-klaviyo-list');
    var emailEl = elLead.querySelector('[data-calc-email]');
    var email = (emailEl && emailEl.value || '').trim();
    if (!key) { showLeadMsg('Configuration courriel manquante.', false); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { showLeadMsg('Entrez une adresse courriel valide.', false); return; }
    if (!lastLines.length) { showLeadMsg('Calculez d’abord votre système.', false); return; }
    var btn = elLead.querySelector('button[type="submit"]');
    if (btn) btn.disabled = true;
    var H = { 'Content-Type': 'application/json', 'revision': '2024-10-15' };
    /* best-effort list subscription (don't block the success message on it) */
    if (listId) {
      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(key), {
        method: 'POST', headers: H,
        body: JSON.stringify({ data: { type: 'subscription', attributes: { profile: { data: { type: 'profile', attributes: { email: email } } } }, relationships: { list: { data: { type: 'list', id: listId } } } } })
      }).catch(function () {});
    }
    /* the event triggers the Klaviyo flow that emails the materials list */
    fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(key), {
      method: 'POST', headers: H,
      body: JSON.stringify({ data: { type: 'event', attributes: { properties: buildQuoteProps(), metric: { data: { type: 'metric', attributes: { name: 'Soumission calculateur clôture' } } }, profile: { data: { type: 'profile', attributes: { email: email } } } } } })
    })
      .then(function (r) {
        if (btn) btn.disabled = false;
        if (r.ok) { showLeadMsg('Merci ! Votre liste de matériel arrive par courriel.', true); if (emailEl) emailEl.value = ''; }
        else { showLeadMsg('Une erreur est survenue. Réessayez dans un instant.', false); }
      })
      .catch(function () { if (btn) btn.disabled = false; showLeadMsg('Une erreur est survenue. Réessayez dans un instant.', false); });
  }

  ready(function () {
    initConductors();
    form.addEventListener('input', function (e) {
      if (e.target && e.target.name === 'efcalc-animal') return;
      compute();
    });
    form.addEventListener('change', function (e) {
      if (e.target && e.target.name === 'efcalc-animal') onAnimal();
      else compute();
    });
    elAdd.addEventListener('click', addAll);
    if (elLead) elLead.addEventListener('submit', leadSubmit);
    if (elStickyAdd) elStickyAdd.addEventListener('click', function () { elAdd.click(); });
    showEmpty();
  });
})();
